import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TerminalsTab } from './TerminalsTab';
import { PaymentIssuesCard } from './PaymentIssuesCard';
import './OverviewTab.css';

const PAYOUT_STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  on_hold: 'On Hold',
  missing_bank_info: 'Missing Bank Info',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
};

const PAYOUT_STATUS_CHIP_CLASS = {
  pending: 'warning',
  approved: 'neutral',
  on_hold: 'warning',
  missing_bank_info: 'negative',
  processing: 'neutral',
  completed: 'positive',
  failed: 'negative',
};

/**
 * Merchant Admin Overview Tab Component
 * Displays merchant-specific metrics with terminals breakdown for Merchant Admins
 */
export function MerchantAdminOverviewTab({ summary, terminals, payouts, paymentIssues, loading, accountId, accountName, onNavigateToTab, onSaveTerminal }) {
  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function formatPayoutDate(dateString) {
    if (!dateString) return '—';
    // Date-only strings (YYYY-MM-DD): parse as local date to avoid TZ shift
    const parts = String(dateString).split('T')[0].split('-');
    const date = parts.length === 3
      ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      : new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  function PayoutStatusChip({ status }) {
    return (
      <span className={`status-chip ${PAYOUT_STATUS_CHIP_CLASS[status] || 'neutral'}`}>
        {PAYOUT_STATUS_LABELS[status] || status || 'Unknown'}
      </span>
    );
  }

  function handleMetricClick(tab) {
    if (onNavigateToTab) {
      onNavigateToTab(tab);
    }
  }

  // Compact Metric Card Component
  function CompactMetricCard({ icon, label, value, subtitle, color = 'primary', clickable, onClick }) {
    return (
      <div
        className={`metric-card-compact ${color} ${clickable ? 'clickable' : ''}`}
        onClick={clickable ? onClick : undefined}
      >
        <div className="metric-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Calculate values for display
  const totalTransactions = parseInt(summary?.total_transactions || 0, 10);
  const paidTransactions = parseInt(summary?.paid_transactions || 0, 10);
  const pendingTransactions = parseInt(summary?.pending_transactions || 0, 10);
  const totalAmountPaid = parseFloat(summary?.total_amount_paid || 0);
  const totalFeesCollected = parseFloat(summary?.total_fees_collected || 0);
  // Total Due = Total Sales - Total Fees (what merchant receives)
  // Use API value if available, otherwise calculate client-side
  const totalDue = summary?.total_due_usd != null
    ? parseFloat(summary.total_due_usd)
    : totalAmountPaid - totalFeesCollected;
  const activeTerminals = (terminals || []).filter(t => t.is_active).length;
  const avgTransactionValue = paidTransactions > 0 ? totalAmountPaid / paidTransactions : 0;

  // Discrepancy metrics (gain/loss from auto-accepted and resolved discrepancies)
  const discrepancySummary = summary?.discrepancy_summary || {};
  const netDiscrepancyUsd = parseFloat(discrepancySummary?.net_discrepancy_usd || 0);
  const autoAccepted = discrepancySummary?.auto_accepted || {};
  const needsResolution = discrepancySummary?.needs_resolution || {};
  const autoAcceptedCount = (parseInt(autoAccepted?.underfunded_count || 0, 10) +
                            parseInt(autoAccepted?.overfunded_count || 0, 10));
  const needsResolutionCount = (parseInt(needsResolution?.underfunded_count || 0, 10) +
                                parseInt(needsResolution?.overfunded_count || 0, 10));
  const hasDiscrepancyData = autoAcceptedCount > 0 || needsResolutionCount > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {accountName ? `${accountName} Overview` : 'Merchant Overview'}
      </Typography>

      {/* Activity Summary Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Activity Summary</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'receipt']}
            label="Total Transactions"
            value={formatNumber(totalTransactions)}
            subtitle="In selected period"
            color="primary"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'check-circle']}
            label="Paid Transactions"
            value={formatNumber(paidTransactions)}
            subtitle={`${pendingTransactions} pending`}
            color="success"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'dollar-sign']}
            label="Total Sales"
            value={formatCurrency(totalAmountPaid)}
            subtitle="Gross revenue"
            color="primary"
          />
          <CompactMetricCard
            icon={['fal', 'wallet']}
            label="Est. Net Proceeds"
            value={formatCurrency(totalDue)}
            subtitle="Sales minus all fees"
            color="success"
          />
          {hasDiscrepancyData && (
            <CompactMetricCard
              icon={['fal', 'balance-scale']}
              label="Discrepancy Impact"
              value={formatCurrency(netDiscrepancyUsd)}
              subtitle={
                needsResolutionCount > 0
                  ? `${needsResolutionCount} need${needsResolutionCount === 1 ? 's' : ''} action`
                  : `${autoAcceptedCount} auto-accepted`
              }
              color={netDiscrepancyUsd >= 0 ? 'success' : 'warning'}
            />
          )}
        </div>
      </div>

      {/* Payment Issues Alert Card */}
      <PaymentIssuesCard
        paymentIssues={paymentIssues}
        onNavigateToTab={onNavigateToTab}
      />

      {/* Fees & Terminals Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Fees & Terminals</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'hand-holding-usd']}
            label="Fees Collected"
            value={formatCurrency(totalFeesCollected)}
            subtitle="All fees, incl. customer-paid"
            color="warning"
          />
          <CompactMetricCard
            icon={['fal', 'tablet-alt']}
            label="Active Terminals"
            value={formatNumber(activeTerminals)}
            subtitle={`${(terminals || []).length} total`}
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'percentage']}
            label="Effective Rate"
            value={totalAmountPaid > 0 ? `${((totalFeesCollected / totalAmountPaid) * 100).toFixed(2)}%` : '0.00%'}
            subtitle="All fees / sales"
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'clock']}
            label="Pending"
            value={formatNumber(pendingTransactions)}
            subtitle="Awaiting payment"
            color={pendingTransactions > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </div>

      {/* Payouts Section */}
      <div className="fi-table-section" style={{ marginTop: '24px' }}>
        <div className="fi-table-header">
          <h3 className="fi-table-title">Payouts</h3>
        </div>
        <div className="fi-table-container">
          <table className="fi-table">
            <thead>
              <tr>
                <th>Payout Date</th>
                <th className="text-right">Amount</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {(payouts || []).length === 0 ? (
                <tr>
                  <td colSpan={3} className="fi-table-empty">
                    <div className="fi-table-empty-icon">
                      <FontAwesomeIcon icon={['fal', 'money-check-alt']} />
                    </div>
                    No payouts yet — payouts are generated after settlement.
                  </td>
                </tr>
              ) : (
                (payouts || []).map((payout) => (
                  <tr key={payout.id}>
                    <td>{formatPayoutDate(payout.payout_date)}</td>
                    <td className="text-right">{formatCurrency(parseFloat(payout.net_amount_usd || 0))}</td>
                    <td className="text-center">
                      <PayoutStatusChip status={payout.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Terminals Section */}
      <Box mt={4}>
        <TerminalsTab terminals={terminals || []} accountId={accountId} onSaveTerminal={onSaveTerminal} />
      </Box>
    </Box>
  );
}
